import { GROCERY_CATEGORIES } from '@recipes/contracts';
import { foldText } from '../text/normalize.js';
/**
 * Keywords per department, folded (`foldText`: lower case, ä → a, ß → ss). How a keyword matches:
 *
 * - `two words`  a phrase, anywhere in the name – the strongest match;
 * - `keyword`    four letters and more: anywhere inside a word ("Vollmilch");
 * - `kw`         up to three letters: a whole word only ("Ei", not "Reis");
 * - `=keyword`   a whole word only, whatever its length;
 * - `-kw`        a whole word or the end of one ("Pfefferminztee").
 */
const KEYWORDS = [
    [
        'produce',
        [
            'obst',
            'gemuse',
            'fruit',
            'vegetable',
            'apple',
            'apfel',
            'banana',
            'banane',
            'lemon',
            'zitrone',
            'lime',
            'limette',
            'orange',
            'mandarine',
            'clementine',
            'grapefruit',
            'nektarine',
            'tomato',
            'tomate',
            'onion',
            'zwiebel',
            'garlic',
            'knoblauch',
            'potato',
            'kartoffel',
            'carrot',
            'karotte',
            'mohre',
            'paprika',
            'bell pepper',
            'red pepper',
            'green pepper',
            'yellow pepper',
            'sweet pepper',
            'chilischote',
            'zucchini',
            'aubergine',
            'eggplant',
            'cucumber',
            'gurke',
            'lettuce',
            'salat',
            'rucola',
            'arugula',
            'spinach',
            'spinat',
            'kale',
            'grunkohl',
            'broccoli',
            'brokkoli',
            'cauliflower',
            'blumenkohl',
            'rosenkohl',
            'wirsing',
            'kohlrabi',
            'mangold',
            'chard',
            'chicoree',
            'radicchio',
            'mushroom',
            'pilz',
            'champignon',
            'pfifferling',
            '=herb',
            '=herbs',
            'krauter',
            'parsley',
            'petersilie',
            'basil',
            'basilikum',
            'dill',
            'mint',
            'minze',
            'cilantro',
            'koriander',
            'chive',
            'schnittlauch',
            'zitronengras',
            'lemongrass',
            'ginger',
            'ingwer',
            'avocado',
            'berry',
            'beere',
            'strawberr',
            'erdbeer',
            'himbeer',
            'heidelbeer',
            'blaubeer',
            'brombeer',
            'johannisbeer',
            'grape',
            'traube',
            'pear',
            'birne',
            'peach',
            'pfirsich',
            'plum',
            'pflaume',
            'zwetschge',
            'cherry',
            'kirsche',
            'mango',
            'papaya',
            'pineapple',
            'ananas',
            'melon',
            'melone',
            'kokosnuss',
            'celery',
            'sellerie',
            'leek',
            'lauch',
            'porree',
            'fennel',
            'fenchel',
            'radish',
            'radieschen',
            'rettich',
            'beet',
            'rote bete',
            'rote beete',
            'pastinake',
            'parsnip',
            'pumpkin',
            'kurbis',
            'squash',
            'maiskolben',
            'corn on the cob',
            'pea',
            'erbse',
            'zuckerschote',
            'sugar snap',
            'bean',
            'bohne',
            'sprossen',
            'sprout',
            'scallion',
            'fruhlingszwiebel',
            'spring onion',
            'shallot',
            'schalotte',
            'cabbage',
            'kohl',
            'asparagus',
            'spargel',
            'sweet potato',
            'susskartoffel',
            'suppengrun',
            'apricot',
            'aprikose',
            'fig',
            'feige',
            'pomegranate',
            'granatapfel',
            'kiwi',
            '=peppers',
            'garlic clove',
            'limone',
            'cress',
            'kresse',
            'butternut',
            'schwammerl',
        ],
    ],
    [
        'bakery',
        [
            'bread',
            'brot',
            'brotchen',
            'semmel',
            'brezel',
            'breze',
            'laugenstange',
            '=roll',
            '=rolls',
            'baguette',
            'toast',
            'zwieback',
            'tortilla',
            'wrap',
            'pita',
            'naan',
            'bagel',
            'croissant',
            'brioche',
            'hefezopf',
            'bun',
            'pie crust',
            'puff pastry',
            'blatterteig',
            'pizzateig',
            'ciabatta',
            'kuchen',
            'cake',
            'torte',
            'muffin',
            'donut',
        ],
    ],
    [
        'meat_fish',
        [
            'fleisch',
            'meat',
            'geflugel',
            'poultry',
            'chicken',
            'hahnchen',
            'huhn',
            'beef',
            '=rind',
            'rinder',
            'pork',
            'schwein',
            'lamb',
            'lamm',
            'duck',
            '=ente',
            'entenbrust',
            'entenkeule',
            'hirsch',
            '=wild',
            '=reh',
            'wurstsalat',
            'fleischsalat',
            'bacon',
            'speck',
            'ham',
            'schinken',
            'sausage',
            'wurst',
            'wurstchen',
            'wiener',
            'aufschnitt',
            'mortadella',
            'chorizo',
            'turkey',
            '=pute',
            'puten',
            'veal',
            'kalb',
            'steak',
            'schnitzel',
            'kotelett',
            'rippchen',
            '=ribs',
            'gulasch',
            'filet',
            'mince',
            'hack',
            'gehacktes',
            'frikadelle',
            'salami',
            'fish',
            'fisch',
            'salmon',
            'lachs',
            'cod',
            'kabeljau',
            'seelachs',
            'zander',
            'dorade',
            'hering',
            'matjes',
            'makrele',
            'mackerel',
            'shrimp',
            'prawn',
            'garnele',
            'scampi',
            'krabben',
            'squid',
            'calamari',
            'tintenfisch',
            'mussel',
            'muschel',
            'trout',
            'forelle',
        ],
    ],
    [
        'dairy_eggs',
        [
            'milk',
            'milch',
            'butter',
            'margarine',
            'cheese',
            'kase',
            'cream',
            'sahne',
            'rahm',
            'schmand',
            'obers',
            'yogurt',
            'yoghurt',
            'joghurt',
            'skyr',
            'quark',
            'topfen',
            'egg',
            'ei',
            'eier',
            'creme fraiche',
            'sour cream',
            'mascarpone',
            'ricotta',
            'mozzarella',
            'parmesan',
            'pecorino',
            'gouda',
            'emmentaler',
            'cheddar',
            'gruyere',
            'gorgonzola',
            'camembert',
            '=brie',
            'fondue',
            'halloumi',
            'feta',
            'cottage',
            'kefir',
            'buttermilk',
            'buttermilch',
        ],
    ],
    [
        'frozen',
        [
            'ice cream',
            'eis',
            'speiseeis',
            'eiscreme',
            'eiswurfel',
            'ice cubes',
            'fish fingers',
            'fischstabchen',
            'pommes',
            'french fries',
            'pizza',
            'fruhlingsrolle',
            'spring roll',
            'rahmspinat',
        ],
    ],
    [
        'baking_goods',
        [
            'mehl',
            'flour',
            'zucker',
            'sugar',
            'backpulver',
            'baking powder',
            'baking soda',
            'natron',
            'hefe',
            'yeast',
            'starke',
            'cornstarch',
            'corn starch',
            'gelatine',
            'gelatin',
            '=agar',
            'fondant',
            'kokosflocken',
            'kakao',
            'cocoa',
            'kuverture',
            'couverture',
            'backschokolade',
            'baking chocolate',
            'chocolate chips',
            'schokotropfen',
            'backaroma',
            'marzipan',
            'streusel',
            'sprinkles',
            'kokosraspel',
            'desiccated coconut',
            'rosine',
            'raisin',
            'sultanine',
            'puddingpulver',
            'tortenguss',
            'sahnesteif',
            'semmelbrosel',
            'paniermehl',
            'breadcrumb',
            'bread crumbs',
            'panko',
            'griess',
            'semolina',
            'nuss',
            '=nut',
            '=nuts',
            'mandel',
            'almond',
            'walnut',
            'hazelnut',
            'cashew',
            'pistazie',
            'pistachio',
            'pinienkern',
            'pine nut',
            'sesam',
            'sesame',
            'mohn',
            'poppy seed',
            'leinsamen',
            'flaxseed',
            'chiasamen',
            'chia seed',
        ],
    ],
    [
        'pasta_rice',
        [
            'nudel',
            'noodle',
            'pasta',
            'spaghetti',
            'penne',
            'fusilli',
            'tagliatelle',
            'lasagne',
            'lasagna',
            'macaroni',
            'makkaroni',
            'farfalle',
            'rigatoni',
            'linguine',
            'orzo',
            'gnocchi',
            'spatzle',
            'tortellini',
            'ravioli',
            'reis',
            'rice',
            'risotto',
            'basmati',
            'milchreis',
            'couscous',
            'bulgur',
            'quinoa',
            'hirse',
            'millet',
            'polenta',
            'linse',
            'lentil',
            'graupen',
            'barley',
            'buchweizen',
            'buckwheat',
            'dinkel',
            'spelt',
            'reispapier',
            'rice paper',
        ],
    ],
    [
        'canned',
        [
            'passata',
            'passierte tomaten',
            'gehackte tomaten',
            'tomaten gehackt',
            'geschalte tomaten',
            'chopped tomatoes',
            'crushed tomatoes',
            'peeled tomatoes',
            'getrocknete tomaten',
            'sun-dried',
            'sundried',
            'tomatenmark',
            'tomato paste',
            'tomato puree',
            'kokosmilch',
            'coconut milk',
            'kichererbse',
            'chickpea',
            'kidneybohne',
            'kidney bean',
            'baked beans',
            'weisse bohnen',
            'schwarze bohnen',
            'black beans',
            'mais',
            'corn',
            'sauerkraut',
            'gewurzgurke',
            'essiggurke',
            'cornichon',
            'pickle',
            'olive',
            'kapern',
            'capers',
            'artischocke',
            'artichoke',
            'apfelmus',
            'applesauce',
            'kompott',
            'preiselbeer',
            'pizzatomaten',
            'thunfisch',
            'tuna',
            'sardine',
            'sardelle',
            'anchov',
            'suppe',
            'soup',
            'eintopf',
        ],
    ],
    [
        'oils_sauces',
        [
            '-ol',
            'oil',
            'essig',
            'vinegar',
            'balsamico',
            'sauce',
            'sosse',
            'ketchup',
            'senf',
            'mustard',
            'mayonnaise',
            'mayo',
            'remoulade',
            'pesto',
            'tabasco',
            'sriracha',
            'worcester',
            'dressing',
            'bruhe',
            'broth',
            'bouillon',
            'fond',
            '=stock',
            'hoisin',
            'teriyaki',
            'tahin',
            'miso',
            'harissa',
            'sambal',
            'curry paste',
            'currypaste',
            'meerrettich',
            'horseradish',
        ],
    ],
    [
        'spices',
        [
            'salt',
            'salz',
            'pepper',
            'pfeffer',
            'peppercorn',
            'pepper flakes',
            'chili flakes',
            'chilli flakes',
            'chiliflocken',
            'chilipulver',
            'cumin',
            'kreuzkummel',
            'kummel',
            'curry',
            'cinnamon',
            'zimt',
            'oregano',
            'thyme',
            'thymian',
            'rosemary',
            'rosmarin',
            'majoran',
            'marjoram',
            'estragon',
            'tarragon',
            'bohnenkraut',
            'chili',
            'cayenne',
            'nutmeg',
            'muskat',
            'muskatnuss',
            'vanilla',
            'vanille',
            'bay leaf',
            'lorbeer',
            'spice',
            'gewurz',
            'turmeric',
            'kurkuma',
            'coriander seed',
            'cardamom',
            'kardamom',
            'clove',
            'nelke',
            'saffron',
            'safran',
            'piment',
            'allspice',
            'sternanis',
            '=anis',
            'senfkorner',
            'garam masala',
            'ras el hanout',
            'knoblauchpulver',
            'garlic powder',
            'zwiebelpulver',
            'onion powder',
            'sumach',
            'krauter der provence',
            'herbes de provence',
            'italienische krauter',
        ],
    ],
    [
        'breakfast',
        [
            'musli',
            'muesli',
            'granola',
            'cornflakes',
            'flakes',
            'flocken',
            'rolled oats',
            '=oats',
            'porridge',
            'cereal',
            'cerealien',
            'marmelade',
            'konfiture',
            'jam',
            'gelee',
            'honig',
            'honey',
            'nutella',
            'nougatcreme',
            'aufstrich',
            'erdnussbutter',
            'peanut butter',
            'erdnussmus',
            'mandelmus',
            'ahornsirup',
            'maple syrup',
        ],
    ],
    [
        'snacks',
        [
            'schokolade',
            'chocolate',
            'keks',
            'cookie',
            'biscuit',
            'chips',
            'crisps',
            'cracker',
            'nachos',
            'gummibar',
            'bonbon',
            'candy',
            'sussigkeit',
            'sweets',
            'popcorn',
            'salzstangen',
            'salzbrezel',
            'riegel',
            'lakritz',
            'licorice',
            'praline',
            'erdnuss',
            'peanut',
            'studentenfutter',
            'waffel',
            'wafer',
            'kaugummi',
            'chewing gum',
        ],
    ],
    [
        'beverages',
        [
            'juice',
            'saft',
            'schorle',
            'water',
            'wasser',
            'sprudel',
            'soda',
            'cola',
            'limo',
            'limonade',
            'lemonade',
            'smoothie',
            'drink',
            'coffee',
            'coffee bean',
            'kaffee',
            'kaffeebohne',
            'espresso',
            'tea',
            'tea bag',
            '-tee',
            'teebeutel',
            'sirup',
            'syrup',
        ],
    ],
    [
        'alcohol',
        [
            'wine',
            'wein',
            'beer',
            'bier',
            'hefeweizen',
            '=pils',
            'pilsner',
            'kolsch',
            '=helles',
            'radler',
            'cidre',
            'cider',
            'sekt',
            'prosecco',
            'champagne',
            'champagner',
            'rum',
            'gin',
            'whisky',
            'whiskey',
            'vodka',
            'wodka',
            'brandy',
            'cognac',
            'tequila',
            'grappa',
            'likor',
            'liqueur',
            'schnaps',
            'obstler',
            'aperol',
            'campari',
            'wermut',
            'vermouth',
            'sherry',
            'marsala',
            'spirituose',
        ],
    ],
    [
        'drugstore',
        [
            'shampoo',
            'duschgel',
            'shower gel',
            'seife',
            'soap',
            'zahnpasta',
            'zahncreme',
            'toothpaste',
            'zahnburste',
            'toothbrush',
            'zahnseide',
            'floss',
            'mundspulung',
            'mouthwash',
            'deo',
            'deodorant',
            'toilettenpapier',
            'klopapier',
            'toilet paper',
            'taschentucher',
            'tissue',
            'feuchttucher',
            'wet wipes',
            'windel',
            'diaper',
            'nappies',
            'rasier',
            'razor',
            'sonnencreme',
            'sunscreen',
            'handcreme',
            'lotion',
            'tampon',
            'binden',
            'wattepad',
            'wattestabchen',
            'cotton pad',
            'pflaster',
            'haarspray',
            'haargel',
        ],
    ],
    [
        'household',
        [
            'foil',
            'folie',
            'paper',
            'papier',
            'paper towel',
            'kitchen roll',
            'kuchenrolle',
            'kuchentucher',
            'parchment',
            'bag',
            'beutel',
            'mullsack',
            'candle',
            'kerze',
            'napkin',
            'serviette',
            'detergent',
            'spulmittel',
            'dish soap',
            'tabs',
            'waschmittel',
            'laundry',
            'weichspuler',
            'softener',
            'reiniger',
            'cleaner',
            'putzmittel',
            'schwamm',
            'sponge',
            'lappen',
            'batterie',
            'battery',
            'batteries',
            'gluhbirne',
            'light bulb',
            'filter',
            'streichholz',
            'streichholzer',
            '=matches',
            'feuerzeug',
            '=lighter',
            'zahnstocher',
            'toothpick',
        ],
    ],
    ['pet', ['futter', 'katzenstreu', 'cat food', 'dog food', 'pet food', 'cat litter', 'leckerli']],
];
/** Anything frozen is in the freezer, whatever it is. */
const FROZEN_MARKERS = /(^|[^a-z])(frozen|tiefgekuhlt|tiefkuhl|gefroren|tk)([^a-z]|$)|tiefkuhl/;
/** "Tomaten (Dose)", "Dosenmais", "canned peaches": food that also comes fresh or dried. */
const CANNED_MARKERS = /(^|[^a-z])(dose|dosen|glas|canned|tinned|konserven?|eingelegt(e[rnms]?)?|pickled)([^a-z]|$)|(^|[^a-z])dosen[a-z]/;
const CAN_BE_CANNED = new Set([
    'produce',
    'meat_fish',
    'pasta_rice',
    'pantry',
]);
const POWDERED_PAPRIKA = /(pulver|powder|edelsuss|rosenscharf|gerauchert|smoked|ground|sweet paprika|hot paprika)/;
/** What may follow a keyword at the end of a word: plural and case endings. */
const ENDINGS = ['', 's', 'e', 'n', 'en', 'er', 'es'];
const RULES = KEYWORDS.flatMap(([category, keywords]) => keywords.map((keyword) => {
    if (keyword.includes(' ') || keyword.includes('-', 1))
        return { category, text: keyword, mode: 'phrase' };
    if (keyword.startsWith('='))
        return { category, text: keyword.slice(1), mode: 'word' };
    if (keyword.startsWith('-'))
        return { category, text: keyword.slice(1), mode: 'suffix' };
    return { category, text: keyword, mode: keyword.length <= 3 ? 'word' : 'inside' };
}));
const endsWith = (word, text) => ENDINGS.some((ending) => word.endsWith(text + ending));
const isWord = (word, text) => ENDINGS.some((ending) => word === text + ending);
/**
 * Rank of a match: a phrase beats everything; then a keyword that ends a word – the head of a
 * German compound is its last part ("Tomatensauce" is a sauce, "Pfefferminztee" a tea) – and
 * the later word ("olive oil" is an oil); then the longer keyword.
 */
function rank(rule, name, words) {
    if (rule.mode === 'phrase')
        return name.includes(rule.text) ? [2, 0, rule.text.length] : null;
    const matches = rule.mode === 'word' ? isWord : endsWith;
    for (let index = words.length - 1; index >= 0; index -= 1)
        if (matches(words[index], rule.text))
            return [1, index, rule.text.length];
    if (rule.mode !== 'inside')
        return null;
    for (let index = words.length - 1; index >= 0; index -= 1)
        if (words[index].includes(rule.text))
            return [0, index, rule.text.length];
    return null;
}
/**
 * The department an item is found in, by keyword (German and English). Anything unknown is
 * "pantry" (other dry goods) – the safest default for food.
 */
export function classifyGroceryCategory(ingredientName) {
    const name = foldText(ingredientName);
    if (FROZEN_MARKERS.test(name))
        return 'frozen';
    const words = name.split(/[^a-z0-9]+/).filter(Boolean);
    let best = null;
    for (const rule of RULES) {
        const found = rank(rule, name, words);
        if (!found)
            continue;
        if (!best ||
            found[0] > best.rank[0] ||
            (found[0] === best.rank[0] &&
                (found[1] > best.rank[1] || (found[1] === best.rank[1] && found[2] > best.rank[2]))))
            best = { rule, rank: found };
    }
    let category = best?.rule.category ?? 'pantry';
    // "Paprika" is the vegetable; the powder is a spice.
    if (best?.rule.text === 'paprika' && POWDERED_PAPRIKA.test(name))
        category = 'spices';
    if (CAN_BE_CANNED.has(category) && CANNED_MARKERS.test(name))
        return 'canned';
    return category;
}
/**
 * The order in which a list shows its departments: the store's own order first, then whatever
 * it leaves out in the default order. Unknown codes (a newer server) are dropped.
 */
export function orderGroceryCategories(custom) {
    const known = new Set(GROCERY_CATEGORIES);
    const first = [...new Set(custom ?? [])].filter((c) => known.has(c));
    return [...first, ...GROCERY_CATEGORIES.filter((c) => !first.includes(c))];
}
/** The finer departments each of the former, coarser ones was split into. */
const SPLIT_INTO = {
    pantry: ['baking_goods', 'pasta_rice', 'canned', 'oils_sauces', 'breakfast', 'snacks'],
    beverages: ['alcohol'],
    household: ['drugstore', 'pet'],
    other: ['drugstore', 'pet', 'alcohol', 'snacks', 'breakfast'],
};
/**
 * For rows stored before the departments were split: the finer department when the name says
 * so, else what is stored. Never moves a row between departments that were not split.
 */
export function refineGroceryCategory(stored, name) {
    const finer = SPLIT_INTO[stored];
    if (!finer)
        return stored;
    const now = classifyGroceryCategory(name);
    return finer.includes(now) ? now : stored;
}
//# sourceMappingURL=grocery-category.js.map